<template>
  <DataTable
    :value="results"
    :rowClass="(data) => getRowClass(data)"
    :scrollHeight="$isLargeScreen.value ? scrollHeight : undefined"
    :scrollable="$isLargeScreen.value"
    responsiveLayout="stack"
  >
    <Column
      v-for="(column, index) in columnsToShow"
      :key="index"
      :header="column.header"
      :field="column.field"
      :sortable="column.sortable"
      :headerStyle="column.headerStyle"
      :style="column.headerStyle"
      :alignFrozen="column.alignFrozen"
      :frozen="column.frozen"
    >
      <template #body="{ data }">
        <span v-if="column.field === 'earring'">
          <router-link
            :to="{ name: 'bovine.edit', params: { id: data.id } }"
            target="_blank"
            style="text-decoration: none"
          >
            {{ data.earring }}</router-link
          >
        </span>

        <span v-if="column.field === 'type'">
          <router-link
            :to="{ name: 'bovine.edit', params: { id: data.id } }"
            target="_blank"
            style="text-decoration: none"
          >
            <BovineType
              small
              :type="data.type"
              :inactive="data?.status?.available === false"
            />
          </router-link>
        </span>

        <span v-if="column.field === 'breed'">
          {{ data?.breed?.name || '-' }}
        </span>

        <span v-if="column.field === 'birthDate'">
          {{
            data.birthDate ? $dayjs(data.birthDate).format('DD/MM/YYYY') : '-'
          }}
        </span>

        <span v-if="column.field === 'weight'">
          {{ data.weight ? $toDecimal(data.weight) : '-' }}
        </span>

        <span v-if="column.field === 'status'">
          {{ data?.status?.name || '-' }}
        </span>

        <span v-if="column.field === 'farmLot'">
          <span v-if="data?.farmLot?.id">
            <router-link
              :to="{
                name: 'lot.edit',
                params: { id: data?.farmLot?.id },
              }"
              target="_blank"
              style="text-decoration: none"
              >{{ data?.farmLot?.name }}</router-link
            >
          </span>
          <span v-else> - </span>
        </span>

        <span v-if="column.field === 'farm'">
          <router-link
            v-if="data?.farm?.name"
            :to="{ name: 'farm.edit', params: { id: data?.farmId } }"
            target="_blank"
            style="text-decoration: none"
            >{{ data?.farm?.name }}</router-link
          >
          <span v-else> - </span>
        </span>

        <span v-if="column.field === 'bovineRoot'">
          <span v-if="data?.bovineRoot?.id">
            <router-link
              :to="{
                name: 'bovine.edit',
                params: { id: data?.bovineRoot?.id },
              }"
              target="_blank"
              style="text-decoration: none"
              >{{ data?.bovineRoot?.earring }}</router-link
            >
          </span>
          <span v-else> - </span>
        </span>

        <span v-if="column.field === 'bullType'">
          {{ data.bullType?.name || '-' }}
        </span>

        <span v-if="column.field === 'owner'">
          <span v-if="data?.owner?.id">
            <router-link
              :to="{
                name: 'owner.edit',
                params: { id: data?.owner?.id },
              }"
              target="_blank"
              style="text-decoration: none"
              >{{ $sliceText(data?.owner?.name, 15) }}</router-link
            >
          </span>
          <span v-else> - </span>
        </span>

        <span v-if="column.field === 'actions'">
          <slot name="actions" :data="data"></slot>
        </span>
      </template>
    </Column>
  </DataTable>
</template>

<script>
import BovineType from './BovineType.vue';

export default {
  name: 'BovineDataTable',
  components: { BovineType },
  props: {
    results: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
    scrollHeight: {
      type: String,
      default: '500px'
    },
  },
  computed: {
    columnsToShow() {
      return this.bovineColumns.filter((bovineColumn) => {
        const column = this.columns.find((c) => c.field === bovineColumn.field);

        if (column) {
          Object.assign(bovineColumn, column);
        }

        return !bovineColumn.hide;
      });
    },
  },
  data() {
    return {
      bovineColumns: [
        {
          header: 'Brinco',
          field: 'earring',
          sortable: true,
          headerStyle: 'min-width: 100px',
          alignFrozen: 'left',
          frozen: true,
        },
        {
          header: 'Tipo',
          field: 'type',
          sortable: true,
          headerStyle: 'min-width: 100px',
          alignFrozen: 'left',
          frozen: true,
        },
        {
          header: 'Raça',
          field: 'breed',
          sortable: true,
          headerStyle: 'min-width: 85px',
        },
        {
          header: 'Data de nascimento',
          field: 'birthDate',
          sortable: true,
          headerStyle: 'min-width: 100px',
        },
        {
          header: 'Peso',
          field: 'weight',
          sortable: true,
          headerStyle: 'min-width: 100px',
        },
        {
          header: 'Status',
          field: 'status',
          sortable: true,
          headerStyle: 'min-width: 100px',
        },
        {
          header: 'Lote/Piquete',
          field: 'farmLot',
          sortable: true,
          headerStyle: 'min-width: 140px',
        },
        {
          header: 'Fazenda',
          field: 'farm',
          sortable: true,
          headerStyle: 'min-width: 130px',
        },
        {
          header: 'Matriz',
          field: 'bovineRoot',
          sortable: true,
          headerStyle: 'min-width: 120px',
        },
        {
          header: 'Touro',
          field: 'bullType',
          sortable: true,
          headerStyle: 'min-width: 110px',
        },
        {
          header: 'Proprietário',
          field: 'owner',
          sortable: true,
          headerStyle: 'min-width: 125px',
        },
        {
          header: 'Ações',
          field: 'actions',
          headerStyle: 'min-width: 100px',
          hide: true,
          alignFrozen: 'right',
          frozen: true,
        },
      ],
    };
  },
  methods: {
    getRowClass(data) {
      return data?.status?.available === false ? 'app-row-inactive' : null;
    },
  },
};
</script>